import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Search, 
  Download, 
  Filter, 
  MoreVertical, 
  Settings, 
  Ban, 
  Clock,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';

interface User {
  id: string;
  email: string;
  displayName: string;
  energyType: string;
  status: 'active' | 'inactive' | 'banned';
  lastActive: Date;
  deviceCount: number;
}

export function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive' | 'banned'>('all');
  const { toast } = useToast();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      
      // Simuler le chargement des données depuis Supabase
      // Dans une implémentation réelle, ces données viendraient de la base de données
      const mockUsers: User[] = [
        {
          id: '1',
          email: 'user1@example.com',
          displayName: 'John Doe',
          energyType: 'Projector',
          status: 'active',
          lastActive: new Date(),
          deviceCount: 2
        },
        {
          id: '2',
          email: 'user2@example.com',
          displayName: 'Jane Smith',
          energyType: 'Generator',
          status: 'inactive',
          lastActive: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          deviceCount: 1
        },
        {
          id: '3',
          email: 'user3@example.com',
          displayName: 'Bob Wilson',
          energyType: 'Manifestor',
          status: 'banned',
          lastActive: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
          deviceCount: 0
        }
      ];

      setUsers(mockUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger la liste des utilisateurs.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-orange-100 text-orange-800';
      case 'banned':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'inactive':
        return <Clock className="w-4 h-4" />;
      case 'banned':
        return <XCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const filteredUsers = users
    .filter(user => filter === 'all' || user.status === filter)
    .filter(user =>
      searchTerm === '' ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.displayName.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const exportUsers = () => {
    const csvContent = [
      ['ID', 'Email', 'Nom', 'Type', 'Statut', 'Dernière activité', 'Appareils'],
      ...filteredUsers.map(user => [
        user.id,
        user.email,
        user.displayName,
        user.energyType,
        user.status,
        format(user.lastActive, 'dd/MM/yyyy HH:mm', { locale: fr }),
        user.deviceCount
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `utilisateurs_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Gestion des utilisateurs</h2>
            <p className="text-gray-600">
              {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={exportUsers}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Exporter
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Filtres */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Rechercher un utilisateur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="border rounded-md px-3 py-2"
              >
                <option value="all">Tous les statuts</option>
                <option value="active">Actifs</option>
                <option value="inactive">Inactifs</option>
                <option value="banned">Bannis</option>
              </select>
            </div>
          </div>

          {/* Liste des utilisateurs */}
          <div className="space-y-4">
            {filteredUsers.map((user) => (
              <Card key={user.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{user.displayName}</span>
                        <span className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 ${getStatusColor(user.status)}`}>
                          {getStatusIcon(user.status)}
                          {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{user.email}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>Type: {user.energyType}</span>
                        <span>
                          Dernière activité: {format(user.lastActive, 'dd/MM/yyyy HH:mm', { locale: fr })}
                        </span>
                        <span>
                          {user.deviceCount} appareil{user.deviceCount > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <Settings className="w-4 h-4" />
                        Gérer
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <Ban className="w-4 h-4" />
                        Bannir
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {filteredUsers.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Aucun utilisateur trouvé
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}